import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

function csvToIds(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * URL-persisted faceted-filter state for the Opportunities listing (ADV-1362).
 * Mirrors `use-jobs-filters.ts`. The salesperson facet keys on `assigned_rep_id`
 * (a contact_role id, 100% populated + name-resolved in the view) per spec D11 —
 * the same column the board card and group-by use. Reads/writes a CSV param and
 * returns a `params` object ready to spread into `useOpportunities(...)`.
 *
 * Both the board and table fetch through this so a selection filters both views;
 * the filter is applied server-side (the board fetches a single capped page, so
 * client-trimming would silently drop rows past it).
 */
export function useOpportunityFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const repIds = useMemo(
    () => csvToIds(searchParams.get("assigned_rep_id")),
    [searchParams],
  );

  const setParam = useCallback(
    (key: string, ids: string[]) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (ids.length === 0) next.delete(key);
          else next.set(key, ids.join(","));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setRepIds = useCallback(
    (ids: string[]) => setParam("assigned_rep_id", ids),
    [setParam],
  );

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (repIds.length > 0) p.assigned_rep_id = repIds.join(",");
    return p;
  }, [repIds]);

  return { repIds, setRepIds, params };
}
